/**
 * Single share entry point — icon (card grids) or labelled button (property
 * page) — that opens a small modal with the standard share options
 * (Facebook, X/Twitter, WhatsApp, LinkedIn, Email, Copy link). Replaces the
 * old approach of showing every share icon inline.
 */
import { useState } from "react";
import { Share2, Facebook, Twitter, Linkedin, MessageCircle, Mail, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { getAgentWhatsAppHref } from "@/utils/whatsapp";

export default function ShareButton({ property, url, content = {}, className = "", size = "md", variant = "icon" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!property?.property_id) return null;

  const rawUrl = url || `${window.location.origin}/property/${property.property_id}`;
  const shareUrl = rawUrl.replace(/https:\/\/[^/]*\.preview\.emergentagent\.com/, "https://selllocalhomes.co.uk");

  const fillTemplate = (tpl) => {
    const t = tpl || "{title} - {price} by {agent}";
    const priceStr = property.price ? `${property.currency || "£"}${Number(property.price).toLocaleString()}` : "";
    const agentStr = property.agent_name || "Sell Local Homes";
    const locationStr = property.display_address || property.cms_town_city || "";
    return t
      .replace(/\{title\}/g, property.title || "property")
      .replace(/\{price\}/g, priceStr)
      .replace(/\{agent\}/g, agentStr)
      .replace(/\{location\}/g, locationStr)
      .trim();
  };

  const fbText = fillTemplate(content.share_facebook_cta);
  const twText = fillTemplate(content.share_twitter_cta);

  // WhatsApp here matches the agent-contact CTA shown above "About this
  // property" — same pre-filled inquiry message, opens a chat with the
  // agent directly instead of a generic "share to any contact" link. Hidden
  // entirely when the agent has WhatsApp disabled, exactly like that CTA.
  const whatsappHref = getAgentWhatsAppHref(property, shareUrl);

  const shareOptions = [
    { key: "facebook", label: "Facebook", icon: Facebook, color: "bg-[#1877F2]", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(fbText)}` },
    { key: "twitter", label: "X / Twitter", icon: Twitter, color: "bg-[#1DA1F2]", href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(twText)}` },
    ...(whatsappHref ? [{ key: "whatsapp", label: "WhatsApp", icon: MessageCircle, color: "bg-[#25D366]", href: whatsappHref }] : []),
    { key: "linkedin", label: "LinkedIn", icon: Linkedin, color: "bg-[#0A66C2]", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` },
    { key: "email", label: "Email", icon: Mail, color: "bg-gray-600", href: `mailto:?subject=${encodeURIComponent(property.title || "Property")}&body=${encodeURIComponent(fbText + " " + shareUrl)}` },
  ];

  const openModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCopied(false);
    setOpen(true);
  };

  const copyLink = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        throw new Error("Clipboard API unavailable");
      }
      setCopied(true);
      toast.success(content.share_copy_cta || "Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for browsers/contexts that block the async Clipboard API
      // (e.g. cross-origin preview iframes without clipboard-write permission).
      try {
        const input = document.createElement("textarea");
        input.value = shareUrl;
        input.style.position = "fixed";
        input.style.opacity = "0";
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
        setCopied(true);
        toast.success(content.share_copy_cta || "Link copied to clipboard!");
        setTimeout(() => setCopied(false), 2000);
      } catch {
        toast.error("Couldn't copy link — please copy it manually");
      }
    }
  };

  const sizeCls = size === "sm" ? "h-7 w-7" : "h-9 w-9";
  const iconCls = size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4";

  return (
    <>
      {variant === "icon" ? (
        <button
          type="button"
          onClick={openModal}
          aria-label="Share this property"
          title="Share this property"
          className={`inline-flex items-center justify-center rounded-full bg-white/90 backdrop-blur-sm shadow-md hover:scale-110 hover:bg-white transition-transform ${sizeCls} ${className}`}
          data-testid={`share-toggle-${property.property_id}-${variant}`}
        >
          <Share2 className={`${iconCls} text-gray-700`} />
        </button>
      ) : (
        <button
          type="button"
          onClick={openModal}
          className={`inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors ${className}`}
          data-testid={`share-toggle-${property.property_id}-${variant}`}
        >
          <Share2 className="h-4 w-4" />
          Share
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        {/* z-[200] beats every other in-app overlay (PropertyModal quick-view
            is z-[100], nested article/media modals go up to z-[180]) so the
            share dialog is never rendered behind a darker backdrop and
            appears invisible/unclickable when triggered from inside one. */}
        <DialogContent
          className="max-w-sm z-[200]"
          overlayClassName="z-[200]"
          data-testid="share-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle>Share this property</DialogTitle>
            <DialogDescription>Choose where to share this property, or copy the link below.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            {shareOptions.map((s) => (
              <a
                key={s.key}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => { e.stopPropagation(); setOpen(false); }}
                className={`flex items-center gap-2 px-3 py-2 rounded text-white text-sm font-medium ${s.color} hover:opacity-90 transition-opacity`}
                data-testid={`share-option-${s.key}`}
              >
                <s.icon className="h-4 w-4 shrink-0" />
                {s.label}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-2 border border-input rounded px-2 py-1.5">
            <input
              readOnly
              value={shareUrl}
              onClick={(e) => e.target.select()}
              className="flex-1 text-xs bg-transparent outline-none text-muted-foreground min-w-0"
              data-testid="share-url-input"
            />
            <button
              type="button"
              onClick={copyLink}
              className="text-xs font-medium text-primary hover:underline shrink-0 inline-flex items-center gap-1"
              data-testid="share-copy-link-button"
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
